import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import { FaBolt, FaChevronDown, FaCircleInfo, FaHouse, FaTrophy, FaUser, FaXmark } from "react-icons/fa6";
import { Tooltip } from "react-tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { MdTouchApp } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import TailwindModal from "./Modals/TailwindModal";

const TailwindSidebar = ({
    leaderboardData,
    data: _data,
    userRank,
    fetchLeaderboard,
    isLeaderboardFetching = false,
    coinsCount,
    isExl,
    onContactUsClick,
    onCollapseChange,
    isGameplay = false,
    task: _task = "welcome",
    successIndex: _successIndex = 0,
    onTaskClick: _onTaskClick,
    isCollapsedProp,
    userDetails,
    onLogout,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isProgressByDomainOpen, setIsProgressByDomainOpen] = useState(false);
    const [isPassFailByDomainOpen, setIsPassFailByDomainOpen] = useState(false);
    const [passFailDetailModal, setPassFailDetailModal] = useState(null);
    const navigate = useNavigate();
    const navRef = useRef(null);

    const handleLogoutClick = async () => {
        setIsProfileOpen(false);
        setIsLoggingOut(true);
        try {
            await onLogout?.();
        } finally {
            setIsLoggingOut(false);
        }
    };

    // Sync internal state with prop when it changes
    useEffect(() => {
        if (isCollapsedProp !== undefined) {
            setIsCollapsed(isCollapsedProp);
        }
    }, [isCollapsedProp]);

    // Close dropdowns when clicking outside the nav
    useEffect(() => {
        if (!isLeaderboardOpen && !isProfileOpen) return;
        const handleClickOutside = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setIsLeaderboardOpen(false);
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isLeaderboardOpen, isProfileOpen]);

    const handleToggleSidebar = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        // Close any open modals when collapsing
        if (newState) {
            setIsLeaderboardOpen(false);
            setIsProfileOpen(false);
        }
        if (onCollapseChange) {
            onCollapseChange(newState);
        }
    };

    const restrictedVerticalTotal = userDetails?.restrictedVerticalPublishedUseCaseTotal;
    const restrictedVerticalPassed = userDetails?.restrictedVerticalCompletionSummary?.passedCount ?? 0;
    const showRestrictedVerticalSummary =
        restrictedVerticalTotal != null && Number.isFinite(Number(restrictedVerticalTotal));
    const certReadinessInfoAnchorId = React.useId().replace(/:/g, "");

    const getDomainLabel = (d) =>
        (d.domainName && String(d.domainName).trim()) ||
        (d.domainSlug &&
            String(d.domainSlug)
                .split(/[-_]/)
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ")) ||
        "Domain";

    const hideFinanceDomain = (d) => String(d.domainSlug || "").toLowerCase() !== "finance";

    return (
        <>
        <div
            className={`relative shrink-0 flex flex-col z-20 transition-all duration-300 ${isCollapsed ? 'w-[84px] shadow-[4px_0_12px_rgba(0,0,0,0.08)]' : 'w-[323px]'} bg-white h-full`}
        >

            {/* ─── COLLAPSED: GameList ─── */}
            {isCollapsed && !isGameplay && (
                <div className="flex flex-col items-center h-full">
                    {/* Expand toggle — sits at same height as header */}
                    <div className="flex items-center justify-center w-full h-[56px] shrink-0 px-4">
                        <button
                            onClick={handleToggleSidebar}
                            className="w-8 h-8 flex items-center justify-center rounded text-exl-midnight-blue hover:bg-exl-light-blue transition-colors"
                        >
                            <ChevronRightIcon fontSize="small" />
                        </button>
                    </div>

                    {/* Nav items */}
                    <div className="flex flex-col items-center gap-8 px-4 pt-6 w-full">
                        {/* Home icon */}
                        <button
                            type="button"
                            onClick={() => navigate("/gamelist")}
                            className="w-12 h-12 flex items-center justify-center rounded-xl bg-exl-light-blue text-exl-midnight-blue hover:bg-exl-light-blue/70 transition-colors"
                            aria-label="Home"
                        >
                            <FaHouse className="text-xl" />
                        </button>

                        {/* Coins */}
                        <div className="flex flex-col items-center gap-1">
                            <FaBolt className="text-exl-xp-green" size={22} />
                            <span className="text-exl-xp-green text-sm font-extrabold font-['Plus_Jakarta_Sans']">
                                {coinsCount}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── COLLAPSED: Gameplay – tasks moved to header; only expand button ─── */}
            {isCollapsed && isGameplay && (
                <div className="flex flex-col items-center h-full py-3">
                    <button
                        onClick={handleToggleSidebar}
                        className="w-7 h-7 flex items-center justify-center rounded text-exl-midnight-blue hover:bg-exl-light-blue transition-colors"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </button>
                </div>
            )}

            {/* ─── EXPANDED ─── */}
            {/* Scrollable Content Area */}
            <div className={`flex-1 overflow-y-auto p-4 flex flex-col ${isCollapsed ? 'hidden' : ''}`}>
            {/* Toggle Button */}
            <button
                onClick={handleToggleSidebar}
                className="absolute top-4 right-2 w-8 h-8 flex items-center justify-center rounded text-exl-midnight-blue z-30 hover:bg-exl-light-blue transition-colors"
            >
                <ChevronLeftIcon className="text-xl" />
            </button>

            {/* Conditional Content: Gameplay Tasks or GameList Content */}
            {!isCollapsed && (
                <>
                    {isGameplay ? (
                        /* Gameplay Mode: Tasks moved to header; sidebar shows only rank/coins */
                        <div className="mb-4" id="tour-tasks-sidebar" />
                    ) : (
                        /* GameList Mode: Home / Leaderboard / Profile nav */
                        <>
                            <nav ref={navRef} className="mb-4 space-y-1">
                                {/* Home */}
                                <button
                                    type="button"
                                    onClick={() => navigate("/gamelist")}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-exl-midnight-blue hover:bg-exl-light-blue/60 transition-colors"
                                >
                                    <FaHouse className="text-lg" />
                                    <span>Home</span>
                                </button>

                                {/* Leaderboard button */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsLeaderboardOpen((open) => {
                                            const next = !open;
                                            if (next) {
                                                fetchLeaderboard?.();
                                            }
                                            return next;
                                        });
                                        setIsProfileOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-exl-midnight-blue hover:bg-exl-light-blue/60 transition-colors"
                                >
                                    <FaTrophy className="text-lg" />
                                    <span>Leaderboard</span>
                                </button>

                                {/* Profile — icon + user name */}
                                <div className="w-full flex items-center gap-3 px-3 py-2 rounded-xl">
                                    <FaUser className="text-lg text-exl-midnight-blue shrink-0" />
                                    <span className="text-sm font-semibold text-exl-midnight-blue font-['Plus_Jakarta_Sans'] truncate">
                                        {userDetails?.name ?? userDetails?.userName ?? "User"}
                                    </span>
                                </div>
                            </nav>

                            {Array.isArray(userDetails?.useCaseProgressByDomain) &&
                                userDetails.useCaseProgressByDomain.filter(hideFinanceDomain).length >
                                    0 && (
                                    <div className="rounded-xl bg-exl-light-blue/60 border border-exl-midnight-blue/[0.06] p-3 mb-1">
                                        <button
                                            type="button"
                                            className="w-full flex items-start gap-2 text-left mb-0"
                                            onClick={() => {
                                                setIsProgressByDomainOpen((wasOpen) => {
                                                    const next = !wasOpen;
                                                    if (next) {
                                                        setIsPassFailByDomainOpen(false);
                                                    }
                                                    return next;
                                                });
                                            }}
                                            aria-expanded={isProgressByDomainOpen}
                                        >
                                            <p
                                                className="font-['Plus_Jakarta_Sans'] text-[10px] font-semibold uppercase tracking-[0.12em] flex-1 min-w-0"
                                                style={{
                                                    color: "black",
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Module Completion Per Domain
                                            </p>
                                            <FaChevronDown
                                                className={`shrink-0 text-exl-midnight-blue/70 text-xs mt-0.5 transition-transform ${isProgressByDomainOpen ? "rotate-180" : ""}`}
                                                aria-hidden
                                            />
                                        </button>
                                        {isProgressByDomainOpen && (
                                            <ul className="space-y-2.5 max-h-[240px] overflow-y-auto pr-0.5 mt-2.5">
                                                {userDetails.useCaseProgressByDomain
                                                    .filter(hideFinanceDomain)
                                                    .map((d) => {
                                                        const label = getDomainLabel(d);
                                                        const total =
                                                            Number(d.totalUseCasesInDomain) || 0;
                                                        const done = Math.min(
                                                            Number(d.useCasesCompleted) || 0,
                                                            total,
                                                        );
                                                        const pct =
                                                            total > 0
                                                                ? Math.round(
                                                                      (done / total) * 100,
                                                                  )
                                                                : 0;
                                                        return (
                                                            <li
                                                                key={d.domainSlug || label}
                                                                className="space-y-1"
                                                            >
                                                                <div className="flex items-baseline justify-between gap-2 min-w-0">
                                                                    <span
                                                                        className="text-xs font-semibold text-exl-midnight-blue truncate font-['Plus_Jakarta_Sans']"
                                                                        title={label}
                                                                    >
                                                                        {label}
                                                                    </span>
                                                                    <span className="text-[11px] font-bold text-exl-midnight-blue/85 shrink-0 tabular-nums">
                                                                        {done}/{total}
                                                                    </span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-exl-midnight-blue/15 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-exl-orange rounded-full transition-all duration-300"
                                                                        style={{
                                                                            width: `${pct}%`,
                                                                        }}
                                                                    />
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                            </ul>
                                        )}
                                    </div>
                                )}

                            {Array.isArray(userDetails?.useCasePassFailByDomain) &&
                                userDetails.useCasePassFailByDomain.filter(hideFinanceDomain)
                                    .length > 0 && (
                                    <div className="rounded-xl bg-exl-light-blue/60 border border-exl-midnight-blue/[0.06] p-3 mb-1">
                                        <button
                                            type="button"
                                            className="w-full flex items-start gap-2 text-left mb-0"
                                            onClick={() => {
                                                setIsPassFailByDomainOpen((wasOpen) => {
                                                    const next = !wasOpen;
                                                    if (next) {
                                                        setIsProgressByDomainOpen(false);
                                                    }
                                                    return next;
                                                });
                                            }}
                                            aria-expanded={isPassFailByDomainOpen}
                                        >
                                            <p
                                                className="font-['Plus_Jakarta_Sans'] text-[10px] font-semibold uppercase tracking-[0.12em] flex-1 min-w-0"
                                                style={{
                                                    color: "black",
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Qualification Status Per Domain
                                            </p>
                                            <FaChevronDown
                                                className={`shrink-0 text-exl-midnight-blue/70 text-xs mt-0.5 transition-transform ${isPassFailByDomainOpen ? "rotate-180" : ""}`}
                                                aria-hidden
                                            />
                                        </button>
                                        {isPassFailByDomainOpen && (
                                            <>
                                                <div
                                                    className="flex items-center justify-center gap-2.5 mt-2 mb-2 px-2 flex-wrap"
                                                    title="Click a domain to see the use cases."
                                                >
                                                    <motion.span
                                                        className="inline-flex text-exl-orange shrink-0"
                                                        aria-hidden
                                                        animate={{ y: [0, -5, 0] }}
                                                        transition={{
                                                            repeat: Infinity,
                                                            duration: 0.95,
                                                            ease: "easeInOut",
                                                        }}
                                                    >
                                                        <MdTouchApp className="text-[22px] opacity-95" />
                                                    </motion.span>
                                                    <p className="text-[10px] font-semibold text-exl-midnight-blue/85 font-['Plus_Jakarta_Sans'] text-center leading-snug max-w-[210px] m-0">
                                                        Click a domain to see the use cases.
                                                    </p>
                                                </div>
                                                <ul className="space-y-3 max-h-[280px] overflow-y-auto pr-0.5 mt-0">
                                                {userDetails.useCasePassFailByDomain.filter(
                                                    hideFinanceDomain,
                                                ).map((d) => {
                                                    const label = getDomainLabel(d);
                                                    const passed =
                                                        Number(d.useCasesPassed) || 0;
                                                    const failed =
                                                        Number(d.useCasesFailed) || 0;
                                                    const outcomes = Array.isArray(
                                                        d.completedUseCasesWithOutcome,
                                                    )
                                                        ? [...d.completedUseCasesWithOutcome]
                                                        : [];
                                                    outcomes.sort((a, b) => {
                                                        const ta = new Date(
                                                            a.completedAt || 0,
                                                        ).getTime();
                                                        const tb = new Date(
                                                            b.completedAt || 0,
                                                        ).getTime();
                                                        return tb - ta;
                                                    });
                                                    return (
                                                        <li
                                                            key={d.domainSlug || label}
                                                            className="space-y-0"
                                                        >
                                                            <button
                                                                type="button"
                                                                className="w-full rounded-lg px-2 py-1.5 -mx-2 text-left transition-colors hover:bg-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-exl-orange/35"
                                                                aria-label={`${label}: ${passed} pass, ${failed} fail. Open assessment details.`}
                                                                onClick={() =>
                                                                    setPassFailDetailModal({
                                                                        domainLabel: label,
                                                                        passed,
                                                                        failed,
                                                                        outcomes,
                                                                    })
                                                                }
                                                            >
                                                                <div className="flex items-baseline justify-between gap-2 min-w-0">
                                                                    <span
                                                                        className="text-xs font-semibold text-exl-midnight-blue truncate font-['Plus_Jakarta_Sans']"
                                                                        title={label}
                                                                    >
                                                                        {label}
                                                                    </span>
                                                                    <span className="text-[10px] font-bold text-exl-midnight-blue/85 shrink-0 tabular-nums leading-tight text-right">
                                                                        <span className="text-emerald-800">
                                                                            {passed} pass
                                                                        </span>
                                                                        <span className="text-exl-midnight-blue/40 mx-0.5">
                                                                            ·
                                                                        </span>
                                                                        <span className="text-red-800">
                                                                            {failed} fail
                                                                        </span>
                                                                    </span>
                                                                </div>
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            </>
                                        )}
                                    </div>
                                )}
                        </>
                    )}
                </>
            )}
            </div>{/* end scrollable */}

            {/* Sticky Footer – expanded only */}
            <div className={`p-4 pt-0 mt-auto shrink-0 ${isCollapsed ? 'hidden' : ''}`}>

                {/* Overall Progress — above rank, GameList mode only (restore completedTaskCount / totalTasks / completionPercentage above if enabling) */}
                {/* {!isCollapsed && !isGameplay && (
                    <div className="rounded-lg w-full flex flex-col p-3 justify-between mb-3 bg-exl-light-blue">
                        <p
                            className="font-['Plus_Jakarta_Sans'] text-xs font-semibold leading-[18px] tracking-[0.15px] mb-1"
                            style={{ color: 'var(--text-color-secondary)' }}
                        >
                            OVERALL PROGRESS
                        </p>
                        <p
                            className="font-['Plus_Jakarta_Sans'] text-[15px] font-semibold leading-normal mb-2"
                            style={{ color: 'var(--text-color)' }}
                        >
                            {completionPercentage}% completed
                        </p>
                        <div className="w-full h-[10px] bg-exl-midnight-blue rounded-[5px] overflow-hidden">
                            <div
                                className="h-full bg-exl-orange rounded-[5px] transition-all duration-300"
                                style={{ width: `${completionPercentage}%` }}
                            />
                        </div>
                    </div>
                )} */}

                {/* Rankings Section */}
                {!isCollapsed && (
                    <div className="w-full shrink-0 pb-4 mb-4">
                        {showRestrictedVerticalSummary && (
                            <div className="flex flex-col gap-2 bg-exl-light-blue/60 p-2.5 rounded-xl w-full mb-3">
                                <div className="flex items-start gap-2">
                                    <button
                                        type="button"
                                        id={certReadinessInfoAnchorId}
                                        className="mt-0.5 shrink-0 inline-flex text-exl-midnight-blue/70 hover:text-exl-midnight-blue transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-exl-orange/50"
                                        aria-label="Certification pass criteria"
                                    >
                                        <FaCircleInfo className="text-[15px]" aria-hidden />
                                    </button>
                                    <Tooltip
                                        anchorId={certReadinessInfoAnchorId}
                                        content="Participants/Learners must complete the assessment with a minimum score of 80% to be considered as PASS"
                                        place="top"
                                        offset={8}
                                        className="react-tooltip max-w-[min(280px,calc(100vw-2rem))] whitespace-normal text-left leading-snug z-[100]"
                                    />
                                    <p
                                        className="font-['Plus_Jakarta_Sans'] text-[15px] font-semibold leading-[18px] tracking-[0.05em] flex-1 min-w-0"
                                        style={{ color: "var(--secondary-color)" }}
                                    >
                                        Certification Readiness:{" "}
                                        <span className="font-bold text-exl-orange tabular-nums">
                                            {restrictedVerticalPassed}/{Number(restrictedVerticalTotal)}
                                        </span>
                                    </p>
                                </div>
                                <p className="font-['Plus_Jakarta_Sans'] text-[15px] font-bold leading-snug text-black">
                                    Note: Complete minimum 6 cases from your domain
                                </p>
                            </div>
                        )}
                        {isGameplay ? (
                            /* Gameplay Mode */
                            <div className="bg-exl-light-blue p-2.5 rounded-[10px] w-full">
                                <div className="flex flex-row items-center">
                                    <p
                                        className="font-['Plus_Jakarta_Sans'] text-sm font-medium leading-[18px] tracking-[-0.5px]"
                                        style={{ color: 'var(--text-color)' }}
                                    >
                                        YOUR RANK{" "}
                                        <span className="font-bold text-exl-orange">{userRank ? userRank : ""}</span>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            /* GameList Mode — compact Your Rank */
                            <div className="flex ">
                            </div>
                        )}
                    </div>
                )}

                {/* XP Card - Show in both collapsed and expanded states */}
                <div className="mb-4" id="tour-xp-card">
                    {isCollapsed ? (
                        /* Collapsed XP - Compact Version */
                        <div className="h-[60px] rounded-2xl bg-exl-light-blue flex flex-col items-center justify-center px-2 box-border w-full">
                            <FaBolt
                                className="text-exl-xp-green shrink-0 mb-1"
                                size={24}
                            />
                            <span className="text-exl-xp-green text-lg font-extrabold leading-5">
                                {coinsCount}
                            </span>
                        </div>
                    ) : (
                        /* Expanded XP - Full Version */
                        <div
                            className="h-[75px] rounded-2xl bg-exl-light-blue flex flex-row items-center justify-evenly px-4 box-border w-full"
                        >
                            <div className="w-[60px] h-[60px] shrink-0 mr-3 flex items-center justify-center">
                                <FaBolt className="text-exl-xp-green" size={36} />
                            </div>
                            <div className="flex flex-col justify-center">
                                <span className="text-exl-xp-green text-[28px] font-extrabold leading-7">
                                    {coinsCount}
                                </span>
                                <span
                                    className="text-[15px] font-semibold leading-5"
                                    style={{ color: 'var(--text-color)' }}
                                >
                                    Total Coins <br /> Available
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Contact Us Button - Sticky footer for both GameList and Gameplay modes */}
                {!isExl && (
                    <button
                        onClick={onContactUsClick}
                        className={`w-full text-white ${isCollapsed ? 'text-xs py-2' : 'text-[15px] py-2.5'} font-bold flex flex-row justify-center items-center mb-4 rounded-[15px] border-none hover:opacity-90 transition-opacity bg-exl-orange`}
                    >
                        {isCollapsed ? 'Contact' : 'Contact Us'}
                    </button>
                )}
                {/* Take / Retake Tutorial Button */}
                {/* {!isGameplay && onTakeTutorial && (
                    <button
                        onClick={onTakeTutorial}
                        className={`w-full ${isCollapsed ? 'text-xs py-2' : 'text-[13px] py-2'} font-semibold flex flex-row justify-center items-center mb-2 rounded-[12px] border border-exl-midnight-blue/20 hover:bg-exl-light-blue transition-colors bg-white text-exl-midnight-blue`}
                    >
                        {isCollapsed
                            ? (hasCompletedTutorial ? 'Retake' : 'Take')
                            : (hasCompletedTutorial ? 'Retake Tutorial' : 'Take Tutorial')}
                    </button>
                )} */}
                {/* Logout Button */}
                {!isGameplay && (
                    <button
                        onClick={handleLogoutClick}
                        className={`w-full ${isCollapsed ? 'text-xs py-2' : 'text-[13px] py-2'} font-semibold flex flex-row justify-center items-center gap-2 mb-3 rounded-[12px] border border-red-200 hover:bg-red-50 transition-colors bg-white text-red-500`}
                    >
                        {isCollapsed ? '→' : 'Logout'}
                    </button>
                )}
                <div className="w-full h-[1px] bg-exl-light-blue my-2"></div>
                <p className="text-black text-xs font-bold text-center">
                    Version 1.0.0
                </p>
                {/* copyright text */}
                <p className="text-black text-xs font-bold text-center">
                    Copyright © 2025 Datalyze.
                </p>
            </div>
        </div>

        <TailwindModal
            isOpen={!!passFailDetailModal}
            onClose={() => setPassFailDetailModal(null)}
            contentClassName="w-full max-w-md"
        >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[min(560px,85vh)] flex flex-col border border-exl-midnight-blue/[0.1]">
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-black/8 bg-gradient-to-r from-exl-light-blue/55 to-exl-light-blue/20 shrink-0">
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-exl-midnight-blue/65 mb-1 font-['Plus_Jakarta_Sans']">
                            Domain
                        </p>
                        <h2 className="text-base font-bold font-['Plus_Jakarta_Sans'] text-exl-midnight-blue leading-snug">
                            {passFailDetailModal?.domainLabel}
                        </h2>
                        <p className="text-xs font-semibold mt-2 font-['Plus_Jakarta_Sans'] tabular-nums">
                            <span className="text-emerald-800">
                                {passFailDetailModal?.passed} pass
                            </span>
                            <span className="text-exl-midnight-blue/35 mx-1">·</span>
                            <span className="text-red-800">
                                {passFailDetailModal?.failed} fail
                            </span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPassFailDetailModal(null)}
                        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-exl-midnight-blue/50 hover:text-exl-midnight-blue hover:bg-white/80 transition-colors"
                        aria-label="Close"
                    >
                        <FaXmark className="text-lg" />
                    </button>
                </div>
                <div className="overflow-y-auto px-5 py-4 flex-1 min-h-0 bg-white">
                    {passFailDetailModal?.outcomes?.length ? (
                        <ul className="space-y-2.5">
                            {passFailDetailModal.outcomes.map((o) => {
                                const ok =
                                    String(o.outcome || "").toLowerCase() === "pass";
                                const dateStr = o.completedAt
                                    ? new Date(o.completedAt).toLocaleString(undefined, {
                                          dateStyle: "medium",
                                          timeStyle: "short",
                                      })
                                    : null;
                                return (
                                    <li
                                        key={
                                            o.problemId ||
                                            `${passFailDetailModal.domainLabel}-${o.useCaseName}`
                                        }
                                        className="rounded-xl border border-exl-midnight-blue/[0.08] bg-exl-light-blue/35 px-3.5 py-3 shadow-sm"
                                    >
                                        <div className="flex items-start gap-2.5">
                                            <span
                                                className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md font-['Plus_Jakarta_Sans'] ${
                                                    ok
                                                        ? "bg-emerald-100 text-emerald-900"
                                                        : "bg-red-100 text-red-900"
                                                }`}
                                            >
                                                {ok ? "Pass" : "Fail"}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-exl-midnight-blue font-['Plus_Jakarta_Sans'] leading-snug">
                                                    {o.useCaseName}
                                                </p>
                                                {/* {o.assessmentOutcome && (
                                                    <p className="text-[11px] text-exl-midnight-blue/70 mt-0.5">
                                                        {o.assessmentOutcome}
                                                    </p>
                                                )} */}
                                                {dateStr && (
                                                    <p className="text-[12px] text-black font-bold mt-1.5">
                                                        {dateStr}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-sm text-exl-midnight-blue/55 text-center py-10 px-2 font-['Plus_Jakarta_Sans'] leading-relaxed">
                            No completed use cases with a pass or fail outcome for this domain yet.
                        </p>
                    )}
                </div>
            </div>
        </TailwindModal>

        {/* Leaderboard centered modal */}
        {isLeaderboardOpen && ReactDOM.createPortal(
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[9999] flex items-center justify-center"
                style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.3)" }}
                onMouseDown={() => setIsLeaderboardOpen(false)}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 20 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-black/8">
                        <div className="flex items-center gap-2">
                            <FaTrophy className="text-exl-midnight-blue text-lg" />
                            <span className="text-base font-bold font-['Plus_Jakarta_Sans'] text-exl-midnight-blue tracking-wide">Leaderboard</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsLeaderboardOpen(false)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Close leaderboard"
                        >
                            <FaXmark />
                        </button>
                    </div>

                    {/* Column headers */}
                    <div className="flex items-center justify-between gap-2 px-5 py-2 bg-exl-light-blue/50">
                        <span className="w-8 text-center text-[11px] font-bold text-exl-midnight-blue uppercase tracking-wider">Rank</span>
                        <span className="flex-1 text-[11px] font-bold text-exl-midnight-blue uppercase tracking-wider">Name</span>
                        <span className="text-[11px] font-bold text-exl-midnight-blue uppercase tracking-wider">Score</span>
                    </div>

                    {/* Rows */}
                    <div className="max-h-[420px] overflow-y-auto divide-y divide-black/5">
                        {isLeaderboardFetching && !leaderboardData?.length ? (
                            <div className="flex flex-col items-center justify-center gap-3 px-5 py-14">
                                <div className="h-8 w-8 rounded-full border-2 border-exl-light-blue border-t-exl-midnight-blue animate-spin" />
                                <p className="text-sm text-exl-midnight-blue/70 font-['Plus_Jakarta_Sans']">
                                    Loading leaderboard…
                                </p>
                            </div>
                        ) : leaderboardData?.length ? (
                            leaderboardData.map((rank, idx) => {
                                const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : null;
                                return (
                                    <div
                                        key={rank.id ?? idx}
                                        className={`flex items-center gap-2 px-5 py-3 transition-colors hover:bg-exl-light-blue/30 ${idx < 3 ? 'bg-exl-light-blue/10' : ''}`}
                                    >
                                        <span className="w-8 text-center text-sm font-bold text-exl-midnight-blue shrink-0">
                                            {medal ?? rank?.rank}
                                        </span>
                                        <span className="flex-1 min-w-0 text-sm font-medium truncate" style={{ color: 'var(--text-color)' }}>
                                            {rank?.userData?.name ?? "-"}
                                        </span>
                                        <span className="text-sm font-bold text-exl-xp-green shrink-0">{rank?.score ?? 0}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-sm text-gray-400 px-5 py-6 text-center font-['Plus_Jakarta_Sans']">
                                {isLeaderboardFetching ? "Loading…" : "No data available."}
                            </p>
                        )}
                    </div>

                    {/* Current user rank footer */}
                    {userRank && (
                        <div className="px-5 py-3 bg-exl-light-blue/40 border-t border-black/8 flex items-center justify-between">
                            <span className="text-xs font-semibold text-exl-midnight-blue font-['Plus_Jakarta_Sans'] uppercase tracking-wide">Your Rank</span>
                            <span className="text-sm font-bold text-exl-orange">{userRank}</span>
                        </div>
                    )}
                </motion.div>
            </motion.div>,
            document.body
        )}

        {/* Logging out overlay */}
        {isLoggingOut && ReactDOM.createPortal(
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
                style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", backgroundColor: "rgba(0,0,0,0.35)" }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex flex-col items-center gap-5 bg-white rounded-2xl px-10 py-8 shadow-2xl"
                >
                    {/* Spinner */}
                    <div className="relative w-14 h-14">
                        <motion.span
                            className="block w-14 h-14 rounded-full border-4 border-exl-light-blue border-t-exl-midnight-blue"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                            style={{ display: "block" }}
                        />
                    </div>

                    {/* Animated dots text */}
                    <LoggingOutText />
                </motion.div>
            </motion.div>,
            document.body
        )}
        </>
    );
};

const LoggingOutText = () => {
    const [dots, setDots] = useState("");
    useEffect(() => {
        const interval = setInterval(() => {
            setDots((d) => (d.length >= 3 ? "" : d + "."));
        }, 400);
        return () => clearInterval(interval);
    }, []);
    return (
        <p className="text-base font-semibold font-['Plus_Jakarta_Sans'] text-exl-midnight-blue tracking-wide min-w-[130px] text-center">
            Logging out<span>{dots}</span>
        </p>
    );
};

export default TailwindSidebar;